import invariant from 'invariant';
import lodash from 'lodash';
import sha1 from 'sha1';

import defined from '~app/core/defined';
import { Model, Types } from '~app/core/model';

import DiagramState from './DiagramState';

const Diagram = Model.createType({
  name: 'Diagram',
  fields: {
    type: Types.STR({ default: 'stockflow' }), // "graph" or "stockflow"
    mode: Types.STR({ default: 'reference' }), // "reference" or "creation"
    language: Types.STR(),
    uuid: Types.STR(),
    name: Types.STR(),
    creator: Types.STR({ default: '' }),
    savedDateTime: Types.STR({ default: '' }),
    instructionHeader: Types.STR(),
    instructions: Types.STR(),
    labels: Types.MAPOF({
      keyType: Types.STR(),
      valueType: Types.STR(),
    }),
    labelOrder: Types.STR(),
    solutionHash: Types.STR(),
    initialModel: Types.MODEL(DiagramState),
    workingModel: Types.MODEL(DiagramState),
    solutionModel: Types.MODEL(DiagramState),

    arrowPositions: Types.TRANSIENT(), // cache for storing calculated arrow positions
    allLabelBankItems: Types.TRANSIENT(), // array that defines the order the items in the label bank
    labelBankItems: Types.TRANSIENT(), // array of items that should appear in the label bank
    arrowAnchors: Types.TRANSIENT(), // object: per-node arrow anchor data
    isModified: Types.TRANSIENT(), // boolean: false when doc was just loaded or saved, changes to true after a modification
    solutionConnectorProps: Types.TRANSIENT(), // object: stores connector/signal properties (polarity and terminals) for performant comparison against working model
    workingModelErrors: Types.TRANSIENT(), // object: stores count of errors, by type, tallied in working model
    solutionModelTotalObjectsCount: Types.TRANSIENT(), // integer: number of total objects in solution
    solutionModelTotalFlowObjectsCount: Types.TRANSIENT(), // integer: number of total flow objects in solution
    connectedSolutionObjects: Types.TRANSIENT(), // object: stores subset of solutionModel.nodes object that only includes nodes connected to other nodes (excludes "decoys")

    // We want this to serialize/deserialize so it's not a TRANSIENT. But it will
    // get removed when saving as it is not intended to appear in the saved json files.
    editMode: Types.STR(), // null, "initial", "solution"
  },
  properties: {
    labelsAsSet() {
      return new Set(Object.keys(this.labels));
    },
  },
});

function isValidLabelOrder(obj) {
  if (obj.labelOrder === 'alpha' || obj.labelOrder === 'random') return [true];
  if (obj.labelOrder.startsWith('fixed:')) {
    const labels = obj.labelOrder.substr(6).split(',');
    const labelSet = new Set(labels);
    if ([...labelSet.values()].length !== labels.length) {
      return [false, 'duplicate-labelorder-labels'];
    }
    const objLabelSet = obj.labelsAsSet;
    for (const v of labels) {
      if (!objLabelSet.has(v)) {
        return [false, 'extra-labelorder-label', { label: v }];
      }
    }
    for (const v of objLabelSet) {
      if (!labelSet.has(v)) {
        return [false, 'missing-labelorder-label', { label: v }];
      }
    }
    return [true];
  }
  return [false, 'unknown-labelorder-value', { value: obj.labelOrder }];
}

const validLabelExpr = /^[A-Za-z0-9_-]+$/;
const modelTypeMap = {
  graph: 'graph',
  conceptual: 'conceptual',
  causal: 'causal',
  stockflow: 'stockflow',
};

const validators = [
  obj =>
    modelTypeMap[obj.type]
      ? [true]
      : [false, 'invalid-type', { type: obj.type }],
  obj =>
    obj.language
      ? [true]
      : [false, 'missing-or-empty-key', { key: 'language' }],
  obj => (obj.uuid ? [true] : [false, 'missing-or-empty-key', { key: 'uuid' }]),
  obj => (obj.name ? [true] : [false, 'missing-or-empty-key', { key: 'name' }]),
  obj =>
    obj.instructions
      ? [true]
      : [false, 'missing-or-empty-key', { key: 'instructions' }],
  obj =>
    lodash.isObject(obj.labels)
      ? [true]
      : [false, 'key-not-an-object', { key: 'labels' }],
  obj =>
    Object.keys(obj.labels).every(s => validLabelExpr.test(s))
      ? [true]
      : [false, 'invalid-label'],
  obj => isValidLabelOrder(obj),
  obj =>
    !obj.solutionModel ||
    !obj.solutionHash ||
    obj.calculateSolutionHash() === obj.solutionHash
      ? [true]
      : [
          false,
          'solution-hash-incorrect',
          { expected: obj.calculateSolutionHash() },
        ],
  obj => (!obj.initialModel ? [true] : obj.initialModel.getIsValid(obj)),
  obj => (!obj.workingModel ? [true] : obj.workingModel.getIsValid(obj)),
  obj =>
    !obj.solutionModel || obj.editMode === 'solution'
      ? [true]
      : obj.solutionModel.getIsValid(obj),
];

Diagram.prototype.getIsValid = function () {
  try {
    for (const v of validators) {
      const [result, ...err] = v(this);
      if (!result) {
        return [result, ...err];
      }
    }
  } catch (e) {
    return [
      false,
      'unknown-exception-occurred',
      { exception: e.message || e.toString() },
    ];
  }
  return [true];
};

Diagram.prototype.calculateSolutionHash = function () {
  invariant(this.solutionModel, 'solutionModel is required');
  const labels = Object.values(this.labels).sort().join('\t');
  const nodes = Object.keys(this.solutionModel.nodes)
    .sort((a, b) => this.labels[a].localeCompare(this.labels[b]))
    .map(
      n =>
        `${this.labels[n]}\t${this.solutionModel.nodes[n].type}\t${
          this.labels[this.solutionModel.nodes[n].connectFrom] ?? ''
        }\t${this.labels[this.solutionModel.nodes[n].connectTo] ?? ''}`,
    )
    .join('\n');
  const signals = [...this.solutionModel.signals]
    .sort((a, b) => {
      if (a.polarity !== b.polarity) return a.polarity < b.polarity ? -1 : 1;
      if (a.connectFrom !== b.connectFrom)
        return this.labels[a.connectFrom] < this.labels[b.connectFrom] ? -1 : 1;
      if (a.connectTo !== b.connectTo)
        return this.labels[a.connectTo] < this.labels[b.connectTo] ? -1 : 1;
      return 0;
    })
    .map(
      n =>
        `${n.polarity}\t${this.labels[n.connectFrom]}\t${
          this.labels[n.connectTo]
        }`,
    )
    .join('\n');

  const soln = `${labels}\n${nodes}\n${signals}`;
  //console.log(soln);
  return sha1(soln);
};

Diagram.fromJsonString = jsonString => {
  let obj;
  try {
    obj = JSON.parse(jsonString);
  } catch (e) {
    return [null, 'json-parse-error', { exception: e.message || e.toString() }];
  }
  let inst;
  try {
    inst = Model.deserialize(obj, Diagram);
  } catch (e) {
    return [
      null,
      'deserialize-error',
      { exception: e.message || e.toString() },
    ];
  }
  const [valid, ...err] = inst.getIsValid();
  if (!valid) {
    return [null, ...err];
  }
  if (inst.solutionModel && !inst.solutionHash) {
    inst = inst.clone({ solutionHash: inst.calculateSolutionHash() });
  }
  return [inst];
};

Diagram.prototype.setModelType = function (type) {
  let out = this;
  if (!defined(this.type) && !defined(type)) return out;
  if (this.type === type) return out;
  out = out.clone({
    type: type,
  });
  return out;
};

Diagram.prototype.setEditMode = function (mode) {
  let out = this;
  if (!defined(this.editMode) && !defined(mode)) return out;
  if (this.editMode === mode) return out;
  if (this.editMode === 'initial') {
    // Previous mode was initial, so move workingModel to initialModel.
    out = this.clone({
      initialModel: this.workingModel,
      workingModel: DiagramState.empty,
    });
  } else if (this.editMode === 'solution') {
    // Previous mode was solution, so move workingModel to solutionModel
    // and regenerate solutionHash.
    out = this.clone({
      solutionModel: this.workingModel,
      workingModel: DiagramState.empty,
    });
    out = out.clone({
      solutionHash: out.calculateSolutionHash(),
    });
  }
  if (mode === 'initial') {
    out = out.clone({
      workingModel: out.initialModel,
    });
  } else if (mode === 'solution') {
    out = out.clone({
      workingModel: out.solutionModel,
      solutionHash: null,
    });
  }
  out = out.clone({
    editMode: mode,
  });
  return out;
};

Diagram.prototype.serializeForSave = function ({ outputType } = {}) {
  const out = Model.serialize(this.setEditMode(null));
  delete out.editMode;

  switch (outputType) {
    case 'creation':
      out.mode = 'creation';
      break;
    case 'reference':
      out.mode = 'reference';
      break;
    case 'assessment':
      out.mode = 'reference';
      delete out.solutionModel;
      break;
    default:
      break;
  }
  return out;
};

export default Diagram;
